<template>
    <Disclosure as="nav" class="relative bg-ink-50/95 backdrop-blur-sm" v-slot="{ open }">
        <div class="absolute inset-x-0 bottom-0 h-px bg-gold-300" aria-hidden="true"></div>
        <div class="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
            <div class="relative flex h-20 items-center justify-between">
                <div class="absolute inset-y-0 left-0 flex items-center sm:hidden">
                    <!-- Mobile menu button-->
                    <DisclosureButton
                        class="relative inline-flex items-center justify-center rounded-lg p-2 text-ink-500 hover:bg-primary-700 hover:text-white focus:ring-2 focus:ring-primary-600 focus:outline-hidden focus:ring-inset">
                        <span class="absolute -inset-0.5"></span>
                        <span class="sr-only">Open main menu</span>
                        <Bars3Icon v-if="!open" class="block size-6" aria-hidden="true" />
                        <XMarkIcon v-else class="block size-6" aria-hidden="true" />
                    </DisclosureButton>
                </div>
                <div class="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                    <a href="/" class="flex shrink-0 items-center">
                        <img class="h-16 w-auto object-contain" src="/images/logo.jpg" alt="logo" />
                    </a>
                    <div class="hidden sm:ml-6 sm:block">
                        <div class="flex space-x-4 h-full items-center">
                            <Menu as="div" class="relative flex justify-start items-center text-left">
                                <div v-for="item in navigation" :key="item.name">
                                    <router-link :to="item.href" v-if="!item.dropdown"
                                        :class="[
                                            item.current ? 'bg-primary-600 text-white' : 'hover:bg-primary-600 hover:text-white',
                                            item.color ? item.color : 'text-ink-800',
                                            'rounded-lg px-3 py-2 text-sm font-medium transition-colors'
                                        ]" :aria-current="item.current ? 'page' : undefined">
                                        {{ item.name }}
                                        <ChevronDownIcon v-if="item.dropdown"
                                            class="inline-block ml-2 -mr-1 h-5 w-5 text-ink-800" aria-hidden="true" />
                                    </router-link>

                                    <div v-else class="relative">
                                        <MenuButton
                                            :class="[item.current ? 'bg-primary-600 text-white' : 'text-ink-800 hover:bg-primary-600 hover:text-white', 'rounded-lg px-3 py-2 text-sm font-medium transition-colors']"
                                            :aria-current="item.current ? 'page' : undefined">
                                            {{ item.name }}
                                            <ChevronDownIcon class="ml-2 -mr-1 h-5 w-5 text-ink-800 inline-block"
                                                aria-hidden="true" />
                                        </MenuButton>
                                        <Transition enter="transition ease-out duration-100"
                                            enter-from="transform opacity-0 scale-95"
                                            enter-to="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leave-from="transform opacity-100 scale-100"
                                            leave-to="transform opacity-0 scale-95">
                                            <MenuItems
                                                class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-ink-100 rounded-xl bg-white shadow-lg ring-1 ring-ink-200 focus:outline-none z-50">
                                                <div class="px-1 py-1">
                                                    <MenuItem v-slot="{ active }" v-for="subItem in item.dropdown.items"
                                                        :key="subItem.name">
                                                        <router-link :to="subItem.href" :class="[
                                                            active ? 'bg-primary-50 text-primary-700' : 'text-ink-900',
                                                            'group flex w-full items-center rounded-lg px-2 py-2 text-sm'
                                                        ]">
                                                            {{ subItem.name }}
                                                        </router-link>
                                                    </MenuItem>
                                                </div>
                                            </MenuItems>
                                        </Transition>
                                    </div>
                                </div>
                            </Menu>
                        </div>
                    </div>
                </div>
                <div v-if="user">
                    <Menu as="div" class="relative flex justify-start items-center text-left">
                        <div>
                            <div class="relative">
                                <MenuButton
                                    class="flex items-center text-sm rounded-full focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:ring-offset-ink-50">
                                    <img class="h-8 w-8 rounded-full ring-2 ring-gold-300" :src="user.avatar || '/images/default-avatar.png'"
                                        alt="User Avatar" />
                                    <ChevronDownIcon class="ml-2 -mr-1 h-5 w-5 text-ink-800 inline-block"
                                        aria-hidden="true" />
                                </MenuButton>
                                <Transition enter="transition ease-out duration-100"
                                    enter-from="transform opacity-0 scale-95" enter-to="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75" leave-from="transform opacity-100 scale-100"
                                    leave-to="transform opacity-0 scale-95">
                                    <MenuItems
                                        class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-ink-100 rounded-xl bg-white shadow-lg ring-1 ring-ink-200 focus:outline-none z-50">
                                        <div class="px-1 py-1">
                                            <MenuItem v-slot="{ active }" v-for="item in userDropdownItems"
                                                :key="item.name">
                                            <a :href="item.href" @click="item.name === 'Logout' ? handleLogout($event) : null" :class="[
                                                active ? 'bg-primary-50 text-primary-700' : 'text-ink-900',
                                                'group flex w-full items-center rounded-lg px-2 py-2 text-sm'
                                            ]">
                                                <component :is="item.icon" class="mr-2 h-5 w-5 text-ink-500" />
                                                <span>{{ item.name }}</span>
                                            </a>
                                            </MenuItem>
                                        </div>
                                    </MenuItems>
                                </Transition>
                            </div>
                        </div>
                    </Menu>
                </div>
                <div v-else class="hidden sm:flex sm:items-center sm:justify-end">
                    <router-link to="/register" class="text-sm font-semibold text-white">
                        <Button variant="primary" size="sm" class="me-2">
                            Register
                        </Button>
                    </router-link>
                    <router-link to="/login" class="text-sm font-semibold text-primary">
                        <Button variant="outlined" size="sm">
                            Login
                        </Button>
                    </router-link>
                </div>
            </div>
        </div>

        <DisclosurePanel class="sm:hidden border-t border-gold-200">
            <div class="space-y-1 px-2 pt-2 pb-3">
                <DisclosureButton v-for="item in navigation" :key="item.name" as="a" :href="item.href"
                    :class="[item.current ? 'bg-primary-600 text-white' : 'text-ink-800 hover:bg-primary-600 hover:text-white', 'block rounded-lg px-3 py-2 text-base font-medium']"
                    :aria-current="item.current ? 'page' : undefined">{{ item.name }}</DisclosureButton>
            </div>
        </DisclosurePanel>
    </Disclosure>
</template>

<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { 
    Bars3Icon,
    BellIcon,
    XMarkIcon,
    AcademicCapIcon,
    UserIcon,
    ArrowLeftEndOnRectangleIcon
} from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import Button from 'primevue/button';
import { useAuth } from '@/composables/useAuth';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getFeaturedCategories } from '@/services/categoryService';
import { ref } from 'vue';

const { getUser, logout } = useAuth();
const router = useRouter();
const user = ref(null);
const categories = ref([]);

const navigation = [
    {
        name: 'Programs', href: '#', current: false, dropdown: {
            items: []
        }
    },
    { name: 'Books', href: '/books', current: false, dropdown: false },
    { name: 'About Us', href: '/about-us', current: false, dropdown: false },
    { name: 'Testimonial', href: '/#testimonials', current: false, dropdown: false },
    { name: 'Contact Us', href: '/contact-us', current: false, dropdown: false },
    { name: 'Privacy Policy', href: '/privacy-policy', current: false, dropdown: false },
];

const userDropdownItems = [
    { name: 'Account', href: '/account', icon: UserIcon },
    { name: 'Learning', href: '/learning', icon: AcademicCapIcon },
    { name: 'Logout', href: '#', icon: ArrowLeftEndOnRectangleIcon }
];

const handleLogout = async (event) => {
    event.preventDefault();
    await logout();
    user.value = null;
    router.push('/');
};

onMounted(() => {
    getUser()
        .then(userData => {
            user.value = userData;
        })
        .catch(error => {
            // console.error('Error fetching user data:', error);
        });

    getFeaturedCategories().then(data => {
        categories.value = data.data;

        navigation[0].dropdown.items = categories.value.map(category => ({
            name: category.name,
            href: `/courses?category=${category.slug}`
        }));
    });
})
</script>